import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { isReservedStoreSlug } from '../config/reservedSlugs.js';
import { useTheme } from '../theme/ThemeContext.jsx';
import { parseStoreAccent, storeAccentCssVariables } from '../theme/storeAccent.js';
import { StorePwaBootstrap } from '../components/store/StorePwaBootstrap.jsx';

function SunIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Nova senha após clique no e-mail (`/:slug/redefinir-senha?token=`). */
export function StoreResetPasswordPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get('token') || '').trim();
  const { scheme, toggle } = useTheme();
  const [tenant, setTenant] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [valid, setValid] = useState(null);
  const [lojaNome, setLojaNome] = useState('');
  const [senha, setSenha] = useState('');
  const [senha2, setSenha2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/catalog`, { skipAuth: true })
      .then((payload) => {
        if (cancelled) return;
        const t = payload?.tenant;
        setTenant(t && typeof t === 'object' ? t : null);
        setLoadErr('');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e.message || 'Não foi possível carregar a loja.');
        setTenant(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug || !token) {
      setValid(false);
      return;
    }
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/customer/password-reset?token=${encodeURIComponent(token)}`, {
      skipAuth: true,
    })
      .then((res) => {
        if (cancelled) return;
        if (res?.valid) {
          setValid(true);
          setLojaNome(String(res.loja_nome || ''));
        } else {
          setValid(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setValid(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const accentStyle = useMemo(
    () => storeAccentCssVariables(parseStoreAccent(tenant?.theme_json)),
    [tenant?.theme_json],
  );

  const nomeLoja = lojaNome || (tenant?.nome ? String(tenant.nome) : slug || 'Loja');
  const lojaPath = slug ? `/${encodeURIComponent(slug)}` : '/';

  if (!slug || isReservedStoreSlug(slug)) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setFormErr('');
    if (!token) {
      setFormErr('Falta o token no link. Abra o endereço completo enviado por e-mail.');
      return;
    }
    if (!/^\d{4,8}$/.test(senha)) {
      setFormErr('A senha deve ter 4 a 8 dígitos (igual ao cadastro na loja).');
      return;
    }
    if (senha !== senha2) {
      setFormErr('As duas senhas não coincidem.');
      return;
    }
    setBusy(true);
    try {
      await api(`/store/${encodeURIComponent(slug)}/customer/password-reset-complete`, {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ token, senha }),
      });
      setDone(true);
      window.setTimeout(() => navigate(lojaPath, { replace: true }), 2200);
    } catch (err) {
      setFormErr(err.message || 'Não foi possível actualizar a senha.');
    } finally {
      setBusy(false);
    }
  }

  const shellCls =
    'min-h-dvh bg-zinc-50 text-zinc-900 transition-colors dark:bg-[#06090c] dark:text-zinc-100';

  return (
    <div className={shellCls} style={accentStyle}>
      <StorePwaBootstrap slug={slug ? String(slug).toLowerCase() : ''} tenant={tenant} />
      <header className="sticky top-0 z-10 border-b border-zinc-200/80 bg-white/80 backdrop-blur dark:border-zinc-800/80 dark:bg-zinc-900/75">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3 px-4 py-3">
          <Link to={lojaPath} className="text-sm font-semibold text-[var(--st-700)] hover:underline dark:text-[var(--st-400)]">
            ← Voltar à loja
          </Link>
          <button
            type="button"
            onClick={toggle}
            aria-label={scheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 dark:border-zinc-600 dark:text-zinc-300"
          >
            {scheme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-10">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400 dark:text-zinc-500">
          Nova senha
        </p>
        <h1 className="mt-2 text-center font-display text-2xl font-bold text-zinc-900 dark:text-zinc-50">
          Redefinir senha
        </h1>
        <p className="mt-2 text-center text-sm text-zinc-600 dark:text-zinc-400">
          {nomeLoja}
        </p>

        {loadErr ? (
          <p className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {loadErr}
          </p>
        ) : null}

        {!token ? (
          <p className="mt-8 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
            Abra o link completo enviado por e-mail (inclui o parâmetro <span className="font-mono">token</span>).
          </p>
        ) : null}

        {token && valid === false ? (
          <div className="mt-8 space-y-4 rounded-3xl border border-zinc-200/90 bg-white p-6 dark:border-zinc-700/80 dark:bg-zinc-900/90">
            <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
              Este link expirou ou já foi utilizado. Peça um novo e-mail na página de recuperação de senha.
            </p>
            <Link
              to={`/${encodeURIComponent(slug)}/recuperar-senha`}
              className="inline-flex w-full justify-center rounded-2xl border border-zinc-300 py-3 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-100 dark:hover:bg-zinc-800"
            >
              Pedir novo link
            </Link>
          </div>
        ) : null}

        {token && valid === true && !done ? (
          <form
            onSubmit={onSubmit}
            className="mt-8 space-y-4 rounded-3xl border border-zinc-200/90 bg-white p-6 shadow-xl dark:border-zinc-700/80 dark:bg-zinc-900/90"
          >
            <p className="text-sm text-zinc-600 dark:text-zinc-400">Escolha uma nova senha numérica (4 a 8 dígitos).</p>
            <label className="block text-sm font-semibold">
              Nova senha
              <div className="mt-1 flex gap-2">
                <input
                  type={show ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="new-password"
                  pattern="\d{4,8}"
                  title="4 a 8 dígitos"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-950"
                  disabled={busy}
                />
                <button type="button" className="shrink-0 rounded-xl border px-3 text-xs" onClick={() => setShow((v) => !v)}>
                  {show ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </label>
            <label className="block text-sm font-semibold">
              Confirmar senha
              <input
                type={show ? 'text' : 'password'}
                inputMode="numeric"
                autoComplete="new-password"
                value={senha2}
                onChange={(e) => setSenha2(e.target.value.replace(/\D/g, '').slice(0, 8))}
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-950"
                disabled={busy}
              />
            </label>
            {formErr ? <p className="text-sm font-medium text-red-600 dark:text-red-400">{formErr}</p> : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-2xl bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] py-3 text-sm font-bold text-white shadow-lg transition hover:brightness-105 disabled:opacity-50"
            >
              {busy ? 'A gravar…' : 'Guardar nova senha'}
            </button>
          </form>
        ) : null}

        {done ? (
          <p className="mt-8 rounded-xl bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
            Senha actualizada. A redireccionar para a loja…
          </p>
        ) : null}

        {token && valid === null ? (
          <p className="mt-10 text-center text-sm text-zinc-500">A verificar o link…</p>
        ) : null}
      </main>
    </div>
  );
}
